/**
 * Bahasa Indonesia — the default. English is the secondary.
 *
 * The terminology here is deliberate, and several strings were changed from the
 * mockups after a language review:
 *
 *   "gerbang kesetiaan"  → "pemeriksaan dukungan sumber"  (kesetiaan reads as LOYALTY)
 *   "penyerapan"         → "pemrosesan"                   (collides with budget absorption)
 *   "lajur"              → "spreadsheet"                  (nobody says lajur)
 *   "Pengawal Kebijakan" → "Penyaring Kebijakan"          (pengawal = bodyguard)
 *   "memindai"           → "membaca teks (OCR)"
 *   "potongan"           → kept out of end-user views entirely
 *
 * Number and time formats stay as the mockups had them: 104.882 · 0,71 · 78,5% · 14.22.
 */
export const id = {
  app: {
    name: "Pustaka Aceh",
    tagline: "Pencarian dokumen pemerintahan Aceh",
    prototype: "Purwarupa",
  },
  nav: {
    studio: "AI Studio",
    collections: "Koleksi",
    sources: "Sumber",
    library: "Pustaka",
    trends: "Tren Kueri",
    conversations: "Percakapan",
    newConversation: "Percakapan baru",
    newNotebook: "+ Notebook baru",
    notebooks: "Notebook",
    signOut: "Keluar",
    back: "Kembali",
    skipToContent: "Lewati ke konten utama",
  },
  home: {
    notebooks: "Notebook saya",
    newNotebook: "+ Notebook baru",
    untitled: "Notebook tanpa judul",
    noNotebooks: "Belum ada notebook. Buat satu untuk mulai memilih sumber.",
    title: "Notebook",
    subtitle: "Kumpulkan sumber ke dalam sebuah notebook, lalu tanyakan isinya.",
    studioTitle: "AI Studio",
    studioSub:
      "Ajukan pertanyaan kepada korpus. Setiap jawaban menyertakan sumber yang dapat ditelusuri.",
    studioFoot: "Mulai percakapan",
    collectionsTitle: "Koleksi",
    collectionsSub:
      "Kelompokkan dokumen menjadi koleksi, unggah berkas baru, dan tentukan mana yang ikut dicari.",
    collectionsFoot: "Kelola koleksi",
    recent: "Percakapan terakhir",
    noRecent: "Belum ada percakapan.",
  },
  trends: {
    eyebrow: "Analitik internal",
    title: "Apa yang sedang dicari?",
    subtitle:
      "Sinyal permintaan baru sejak pelacakan diaktifkan—tanpa membaca ulang riwayat percakapan lama.",
    summary: "Ringkasan tren kueri",
    tracked: "Kueri terlacak",
    classified: "Terklasifikasi",
    attributed: "Memiliki sumber",
    sourceEyebrow: "Distribusi sumber",
    sources: "Sumber yang paling banyak dipakai",
    categoryEyebrow: "Enam domain tetap",
    categories: "Tema yang mendominasi",
    keywordEyebrow: "Bahasa permintaan",
    keywords: "Kata kunci teratas",
    queryShare: "bagian dari semua kueri",
    unclassified: "{{count}} belum terklasifikasi",
    noSources: "Belum ada jawaban yang dapat diatribusikan ke sumber.",
    noCategories: "Belum ada kategori yang berhasil ditetapkan.",
    noKeywords: "Belum ada kata kunci yang dapat dihitung.",
    emptyTitle: "Pelacakan baru dimulai",
    emptyBody: "Kueri baru akan muncul di sini setelah jawabannya selesai diproses.",
    forwardOnly: "Pelacakan ke depan saja · tanpa backfill",
    error: "Ringkasan tren belum dapat dimuat. Coba lagi.",
    forbiddenTitle: "Dashboard ini untuk operator",
    forbiddenBody: "Masuk sebagai admin atau operator untuk melihat agregat penggunaan.",
  },
  login: {
    title: "Masuk",
    subtitle: "Akun sementara selama integrasi SSO belum tersedia.",
    username: "Nama pengguna",
    password: "Kata sandi", // pragma: allowlist secret
    submit: "Masuk",
    submitting: "Memeriksa…",
    // The mockup promised "every answer names its source, or is not given" — an
    // absolute the product breaks on three of its own screens. This says what is true.
    promise:
      "Jawaban disusun dari dokumen yang terindeks dan menyertakan rujukan sumbernya. Pernyataan yang tidak didukung sumber ditandai, bukan disembunyikan.",
    personas: "Akun purwarupa",
    personasHelp: "Pilih akun, lalu masukkan kata sandi.",
    persona_admin: "Kelola koleksi dan hapus koleksi",
    persona_operator: "Unggah dan pantau pemrosesan dokumen",
    persona_pengguna: "Bertanya dan melihat percakapan sendiri",
  },
  scope: {
    bar: "Cakupan",
    edit: "Ubah",
    all: "Seluruh Pustaka Aceh",
    allWithCount: "Seluruh Pustaka Aceh ({{count}} dokumen)",
    pustakaOff: "Pustaka Aceh dinonaktifkan",
    collections_one: "{{count}} koleksi",
    collections_other: "{{count}} koleksi",
    documents_one: "{{count}} dokumen",
    documents_other: "{{count}} dokumen",
    empty: "Tidak ada sumber yang dipilih",
    emptyHelp:
      "Pilih setidaknya satu koleksi atau dokumen. Tanpa sumber, pertanyaan tidak dapat dijawab.",
    pickerTitle: "Pilih sumber",
    pickerSearch: "Cari judul dokumen…",
    pickerApply: "Terapkan",
    pickerCancel: "Batal",
    selected: "Terpilih",
    clear: "Kosongkan",
    sourceGroup: "Rumpun sumber",
    // The rail groups sources by WHOSE they are, which is the question a reader
    // actually has when choosing what to search.
    groupCorpus: "Korpus bersama",
    groupShared: "Dibagikan",
    groupMine: "Koleksi saya",
    status: "Status",
    collection: "Koleksi",
    resultsFor: "{{shown}} dari {{total}} dokumen",
    // Every turn is stamped with the scope it ran under, so an old answer cannot be
    // attributed to a scope the reader can no longer reconstruct.
    stamp: "Dijawab dengan cakupan: {{scope}}",
    // A stored conversation does not record the scope its answers ran under, so a
    // replayed turn says so rather than printing an empty stamp.
    stampUnknown: "Cakupan saat jawaban ini dibuat tidak tersimpan.",
  },
  chat: {
    placeholder: "Ajukan pertanyaan tentang dokumen pemerintahan Aceh…",
    send: "Kirim",
    newChat: "Chat baru",
    stop: "Hentikan",
    hint: "Enter untuk mengirim · Shift+Enter untuk baris baru",
    waiting: "Menyusun jawaban",
    waitingHelp:
      "Model membaca dokumen, memeriksa dukungan sumber, lalu menyaring kebijakan. Jawaban muncul setelah pemeriksaan selesai.",
    elapsed: "{{seconds}} dtk",
    you: "Pertanyaan Anda",
    stopped: "Dihentikan sebelum jawaban selesai.",
    emptyTitle: "Belum ada pertanyaan",
    emptyBody:
      "Ajukan pertanyaan di bawah. Jawaban akan disusun dari dokumen dalam cakupan yang Anda pilih.",
  },
  answer: {
    heading: "Jawaban",
    historyWindowTitle: "Pertanyaan sebelumnya tidak terlihat",
    historyWindowBody_one:
      "Saya dapat melihat {{count}} pertanyaan sebelumnya dalam chat ini. Sebagian pertanyaan lama tidak terlihat. Jika Anda merujuk sesuatu yang lebih awal, ulangi poinnya atau mulai chat baru.",
    historyWindowBody:
      "Saya dapat melihat {{count}} pertanyaan sebelumnya dalam chat ini. Sebagian pertanyaan lama tidak terlihat. Jika Anda merujuk sesuatu yang lebih awal, ulangi poinnya atau mulai chat baru.",
    supported: "{{supported}} dari {{total}} pernyataan didukung sumber",
    droppedNone: "Semua pernyataan didukung sumber",
    // Includes missing/unplaceable/unresolved provenance and pre-signal stored turns.
    gateUnknown: "Dukungan sumber belum sepenuhnya terverifikasi untuk jawaban ini.",
    // NOT "dihapus". The gate drops a claim from the CITED set; the sentence itself
    // stays in the answer. Saying "deleted" described a deletion the reader could see
    // had not happened — the statements were plainly still on screen.
    dropped_one: "{{count}} pernyataan tidak didukung sumber",
    dropped_other: "{{count}} pernyataan tidak didukung sumber",
    unverified: "tidak didukung sumber",
    lateGate:
      "Pemeriksaan dukungan sumber berjalan setelah teks dikirim, sehingga penandaan muncul belakangan.",
    fallbackOverRefusal:
      "Pemeriksaan dukungan sumber menolak terlalu banyak pernyataan, sehingga jawaban ringkas ini yang ditampilkan.",
    // The answer IS shown now, so this says what is true of it: nothing in it was
    // checked against a source. Marked, not hidden.
    // The amber panel's TITLE. It used to reuse "Jawaban"/"Answer", which reads as
    // a heading for the answer rather than a warning about it.
    fallbackTitleNoClaims: "Belum diverifikasi",
    fallbackTitleUnverifiedNoClaims: "Jawaban ditahan",
    fallbackTitleOverRefusal: "Sebagian besar pernyataan tidak didukung",
    fallbackTitleCoverageIncomplete: "Cakupan sumber belum lengkap",
    fallbackTitleUnresolvedCitation: "Rujukan sumber tidak dapat ditemukan",
    fallbackTitleUnparseableCompletion: "Format jawaban tidak dapat diverifikasi",
    fallbackTitleAgentOutputLimit: "Jawaban terlalu panjang untuk diselesaikan",
    fallbackTitleUnknown: "Status verifikasi tidak dikenali",
    fallbackNoClaims:
      "Jawaban ini tidak memuat pernyataan yang dapat diperiksa, sehingga belum diverifikasi terhadap sumber.",
    fallbackUnverifiedNoClaims:
      "Pernyataan dalam jawaban tidak dapat diverifikasi dari sumber yang ditemukan, sehingga jawaban tidak ditampilkan.",
    fallbackCoverageIncomplete:
      "Catatan dukungan sumber tidak mencakup seluruh jawaban, sehingga jawaban tidak ditampilkan.",
    fallbackUnresolvedCitation:
      "Jawaban menunjuk sumber yang tidak tersedia pada pemeriksaan ini, sehingga jawaban tidak ditampilkan.",
    fallbackUnparseableCompletion:
      "Model mengembalikan format jawaban yang tidak valid, sehingga konten yang belum diverifikasi tidak ditampilkan.",
    fallbackAgentOutputLimit:
      "Jawaban mencapai batas panjangnya sebelum selesai, sehingga tidak ditampilkan. Bertanya ulang memberi hasil yang sama; tanyakan satu bagian pertanyaan saja.",
    partialTitle: "Jawaban sebagian",
    partialBody:
      "Hanya pernyataan yang terverifikasi oleh sumber yang ditampilkan. Catatan di akhir jawaban menyebut bagian yang tidak dapat dijawab dari dokumen.",
    fallbackUnknown:
      "Jawaban diturunkan karena alasan verifikasi yang belum dikenali. Periksa sumber sebelum menggunakannya.",
    guardBlocked: "Jawaban ditahan oleh Penyaring Kebijakan",
    guardBody:
      "Pertanyaan atau jawaban tidak memenuhi kebijakan komunikasi pemerintah. Coba rumuskan kembali pertanyaan Anda.",
    copy: "Salin",
    copied: "Tersalin",
    copyWithSources: "Salin dengan sumber",
    print: "Cetak",
    like: "Bermanfaat",
    dislike: "Tidak bermanfaat",
    comment: "Beri catatan",
    sources: "Sumber",
  },
  // /query/v2: the whole answer is shown and every claim is marked. Only "terverifikasi"
  // is a positive state; everything else, including no mark at all, is not verified.
  answerV2: {
    mode: "Mode jawaban",
    modeV2: "Mode v2 (anotasi)",
    modeV1: "Mode v1",
    modeLocked:
      "Notebook ini sudah dijawab dengan {{mode}}. Mulai notebook baru untuk memakai mode lain.",
    verdict: {
      supported: "terverifikasi",
      unsupported: "tidak didukung sumber",
      unverified: "belum diverifikasi",
      uncited: "tanpa sumber",
      invalid_citation: "rujukan tidak valid",
      from_conversation: "dari percakapan, bukan dokumen",
    },
    supportedTitle: "Terverifikasi: sumber yang dirujuk mendukung pernyataan ini.",
    unsupportedTitle: "Tidak didukung sumber: sumber yang dirujuk tidak mendukung pernyataan ini.",
    unverifiedTitle: "Belum diverifikasi: pernyataan ini belum diperiksa terhadap sumbernya.",
    unverifiedTitleReason: "Belum diverifikasi ({{reason}}): pernyataan ini belum diperiksa terhadap sumbernya.",
    reason: {
      deadline: "batas waktu habis",
      budget: "anggaran pemeriksaan habis",
      judge_unavailable: "penilai tidak tersedia",
      unparseable: "putusan penilai tidak terbaca",
      disputed: "pemeriksaan tidak sepakat",
    },
    disputedTitle:
      "Belum diverifikasi ({{reason}}): satu pemeriksaan menilai sumber yang dirujuk tidak mendukung pernyataan ini, sedangkan pemeriksaan ulang menilai sumber itu mendukungnya.",
    uncitedTitle: "Tanpa sumber: pernyataan ini tidak merujuk dokumen apa pun.",
    invalidCitationTitle: "Rujukan tidak valid: sumber yang dirujuk tidak ada di antara sumber yang dibaca.",
    fromConversationTitle:
      "Dari percakapan, bukan dokumen: mengulang isi percakapan ini; tidak ada dokumen yang memastikannya.",
    bannerNotVerified_one:
      "{{count}} pernyataan belum terverifikasi — periksa sumbernya sebelum digunakan.",
    bannerNotVerified_other:
      "{{count}} pernyataan belum terverifikasi — periksa sumbernya sebelum digunakan.",
    bannerFromConversation_one:
      "{{count}} pernyataan berasal dari percakapan sebelumnya, bukan dari dokumen.",
    bannerFromConversation_other:
      "{{count}} pernyataan berasal dari percakapan sebelumnya, bukan dari dokumen.",
    bannerUnchecked: "Jawaban ini belum diperiksa.",
    bannerUncheckedBody:
      "Tidak ada bagian jawaban ini yang terverifikasi terhadap sumber. Periksa sumbernya sebelum digunakan.",
    bannerNoClaims: "Jawaban ini tidak memuat pernyataan yang diperiksa terhadap sumber.",
    bannerUnlocated: "Sebagian teks jawaban ini tidak terkait dengan pernyataan yang diperiksa dan belum diverifikasi.",
    bannerNotFull: "Jawaban ini belum sepenuhnya terverifikasi.",
    footSupported: "{{supported}} dari {{total}} pernyataan didukung sumber",
    footUnchecked: "Belum diperiksa terhadap sumber",
    footNoClaims: "Tidak ada pernyataan faktual untuk diperiksa",
    refusal: {
      query_budget_exhausted: {
        title: "Batas penelusuran tercapai",
        body: "Penelusuran mencapai batas panggilan model sebelum jawaban selesai disusun. Persempit pertanyaan atau cakupan, lalu coba lagi.",
      },
      context_budget_exhausted: {
        title: "Sumber terlalu besar untuk dijawab",
        body: "Sumber yang dibutuhkan pertanyaan ini tidak muat dalam konteks model, sehingga jawaban dihentikan sebelum ditulis dan tidak ada yang diperiksa. Persempit cakupan atau pertanyaan.",
      },
      model_timeout: {
        title: "Waktu habis",
        body: "Model tidak selesai menjawab dalam batas waktu. Coba lagi sebentar lagi.",
      },
      mcp_source_unavailable: {
        title: "Sumber data langsung tidak tersedia",
        body: "Sumber data langsung yang dibutuhkan tidak dapat dihubungi, sehingga jawaban tidak disusun.",
      },
      source_out_of_scope: {
        title: "Di luar cakupan sumber",
        body: "Pertanyaan ini membutuhkan sumber di luar cakupan notebook. Tambahkan sumber yang sesuai, lalu coba lagi.",
      },
      source_scope_empty: {
        title: "Belum ada dokumen terindeks di sumber ini",
        body: "Maaf, sumber yang Anda pilih belum memiliki dokumen yang terindeks, sehingga pertanyaan ini tidak dapat dijawab dari sumber tersebut. Pilih sumber lain atau tunggu sampai dokumen selesai diindeks.",
      },
      source_scope_unavailable: {
        title: "Cakupan sumber tidak dapat dibaca",
        body: "Cakupan sumber notebook ini tidak dapat diperiksa saat ini, sehingga jawaban tidak disusun.",
      },
      unparseable_completion: {
        title: "Format jawaban tidak dapat diverifikasi",
        body: "Model mengembalikan format jawaban yang tidak valid, sehingga konten yang belum diverifikasi tidak ditampilkan.",
      },
    },
  },
  evidence: {
    title: "Dasar jawaban",
    fromIndexed: "Dari dokumen terindeks",
    fromLive: "Dari sumber data langsung",
    liveReadNote:
      "Dibaca langsung saat pertanyaan diajukan — tidak diindeks. Buka sumber atau jalankan ulang panggilan di atas untuk memeriksa angkanya.",
    emptyTitle: "Belum ada dasar jawaban",
    emptyBody: "Sumber yang dipakai sebuah jawaban akan muncul di sini.",
    page: "hlm. {{page}}",
    open: "Buka dokumen",
    noText: "Teks sumber tidak tersedia untuk kutipan ini.",
    indexed: "Terindeks",
    more: "Selengkapnya",
    less: "Ringkas",
    openDocument: "Buka dokumen asli",
    toolCall: "Panggilan alat",
    toolArgs: "Argumen",
    toolHost: "Sumber",
    toolFetched: "Diambil",
    toolRows: "Baris",
    // Tool answers carry citations: [] — the CALL is the citation. Saying so plainly
    // is the difference between provenance and a bug.
    toolNote: "Alat ini menjawab langsung dari sumber data; panggilannya adalah rujukannya.",
  },
  trace: {
    working: "Bekerja…",
    trail: "Jejak penelusuran",
    distinct_one: "{{count}} panggilan berbeda",
    distinct_other: "{{count}} panggilan berbeda",
    guardBlocked: "ditahan — jawaban tidak ditampilkan",
    guardPassed: "lolos — jawaban boleh ditampilkan",
    repeatedHelp: "Agen mengulang panggilan yang sama persis dengan argumen yang sama; hasilnya juga sama.",
    title: "Langkah agen",
    round: "Panggilan model {{n}}",
    receipt_one: "{{count}} langkah",
    receipt_other: "{{count}} langkah",
    show: "Tampilkan langkah",
    hide: "Sembunyikan langkah",
    searching: "mencari dokumen",
    reading: "membaca spreadsheet",
    portal: "membaca portal data",
    done: "selesai",
  },
  reader: {
    title: "Dokumen sumber",
    passage: "Kutipan yang dipakai",
    original: "Berkas asli",
    absentTitle: "Berkas asli tidak tersedia di lingkungan ini",
    absentBody: "Baris dokumennya ada, tetapi berkasnya tidak tersimpan di sini.",
    cannotEmbed: "Peramban tidak dapat menampilkan berkas ini.",
    notRenderable: "Format ini tidak dapat ditampilkan di peramban",
    download: "Unduh berkas",
    landedPage: "Dibuka pada halaman {{page}}",
    citedRange: "Bagian yang disorot adalah rentang yang dikutip",
    // Fallback jujur (#533): berkas terbuka, tetapi titik kutipan tidak diketahui.
    noLocation: "Lokasi persis kutipan ini dalam berkas asli tidak tersedia; dokumen ditampilkan dari awal",
  },
  collections: {
    title: "Koleksi",
    subtitle: "Kelompokkan dokumen, unggah berkas, dan tentukan cakupan pencarian.",
    create: "Koleksi baru",
    createTitle: "Buat koleksi",
    name: "Nama",
    namePlaceholder: "mis. Rancangan Qanun 2026",
    description: "Keterangan",
    descriptionPlaceholder: "Opsional — untuk apa koleksi ini.",
    orgUnit: "Perangkat Daerah",
    orgUnitHelp: "Opsional. Menjadi nilai bawaan untuk dokumen yang diunggah ke sini.",
    positionLevel: "Jenjang jabatan minimum",
    confidential: "Tandai sebagai rahasia",
    confidentialHelp:
      "Dokumen yang diunggah ke koleksi ini ditandai rahasia secara bawaan. Pembatasan diberlakukan per dokumen saat pencarian.",
    submit: "Buat",
    submitting: "Membuat…",
    cancel: "Batal",
    nameTaken: "Nama koleksi sudah dipakai.",
    delete: "Hapus koleksi",
    deleteConfirm: "Hapus koleksi “{{name}}”?",
    // A collection is a LABEL, not a container. Deleting it must not read as deleting
    // its documents, because it does not.
    deleteBody:
      "Dokumen di dalamnya tidak ikut terhapus — dokumen hanya kehilangan label koleksi ini dan tetap dapat dicari.",
    deleteSubmit: "Hapus",
    documentCount_one: "{{count}} dokumen",
    documentCount_other: "{{count}} dokumen",
    documentCountZero: "Belum ada dokumen terindeks",
    pendingNote: "Berkas yang masih diproses belum dihitung.",
    emptyTitle: "Belum ada koleksi",
    emptyBody: "Buat koleksi untuk mengelompokkan dokumen dan mengatur cakupan pencarian.",
    adminOnly: "Hanya administrator yang dapat menghapus koleksi.",
    shared: "Korpus bersama",
    sharedNote: "Dikelola sistem — tidak dapat dihapus dari sini.",
  },
  addSources: {
    title: "Tambah sumber",
    wholeCorpus: "Seluruh Pustaka Aceh",
    wholeCorpusSub: "{{total}} dokumen — termasuk yang ditambahkan kemudian",
    collections: "Koleksi",
    documents: "Dokumen",
    alreadyAdded: "sudah ditambahkan",
    noDocuments: "Tidak ada dokumen yang cocok.",
    moreExist: "Menampilkan {{shown}} dari {{total}}. Persempit pencarian untuk melihat sisanya.",
    add: "Tambahkan {{count}} sumber",
  },
  notebook: {
    rename: "Klik untuk mengganti nama",
    untitled: "Notebook tanpa judul",
    namePlaceholder: "Nama notebook",
    notFoundTitle: "Notebook tidak ditemukan",
    notFoundBody:
      "Notebook ini tidak ada, atau akun Anda tidak memiliki akses ke notebook ini. Buat notebook baru untuk bertanya.",
    removeCollection: "Hapus koleksi {{name}} dari sumber",
    moreInCollection: "Menampilkan {{shown}} dari {{total}} dokumen.",
    addSources: "+ Tambah sumber",
    wholePustaka: "Seluruh Pustaka Aceh · {{total}} dokumen",
    sources: "Sumber",
    attachPustaka: "Tambahkan seluruh Pustaka ({{total}} dokumen)",
    detachPustaka: "Lepaskan seluruh Pustaka",
    unfiled: "Tanpa koleksi",
    wholeCollection: "seluruh koleksi",
    liveCollection: "Seluruh isi koleksi ini ikut dicari, termasuk dokumen yang ditambahkan kemudian.",
    remove: "Hapus",
    removeDocument: "Hapus {{title}} dari sumber",
    excluded: "{{count}} dikecualikan",
    moved: "{{count}} sumber mengikuti versi terbaru dokumennya.",
    missing: "{{count}} sumber tidak lagi tersedia.",
    empty: "Belum ada sumber. Tambahkan koleksi atau dokumen sebelum bertanya.",
  },
  library: {
    facetSearch: "Cari…",
    facetNoMatch: 'Tidak ada yang cocok dengan "{{needle}}".',
    allRegions: "Semua wilayah",
    allYears: "Semua tahun",
    title: "Pustaka",
    subtitle: "Seluruh dokumen terindeks yang dapat Anda akses.",
    search: "Cari judul…",
    upload: "Unggah dokumen",
    uploadTitle: "Unggah dokumen",
    uploadDrop: "Letakkan berkas di sini atau klik untuk memilih",
    uploadFormats: "PDF · DOCX · XLSX · CSV · HTML · gambar hasil pindaian",
    uploadBrowse: "Pilih berkas",
    uploadTo: "Masukkan ke koleksi",
    uploadNone: "Tanpa koleksi",
    uploadSourceType: "Rumpun sumber",
    uploadSubmit: "Unggah",
    uploadSubmitting: "Mengunggah…",
    uploadQueued: "{{count}} berkas masuk antrean pemrosesan.",
    uploadStatus: "Status unggahan",
    columns: {
      title: "Judul",
      year: "Tahun",
      region: "Wilayah",
      source: "Rumpun sumber",
      format: "Format",
      status: "Status",
      collection: "Koleksi",
    },
    status: {
      pending: "Menunggu",
      processing: "Diproses",
      indexed: "Terindeks",
      needs_review: "Perlu ditinjau",
      failed: "Gagal",
      archived: "Diarsipkan",
    },
    ocrReading: "membaca teks (OCR)",
    emptyTitle: "Tidak ada dokumen",
    emptyBody: "Tidak ada dokumen yang cocok dengan filter ini.",
    total: "{{shown}} dari {{total}} dokumen",
    more: "Muat lebih banyak",
  },
  collection: {
    documents: "Dokumen dalam koleksi",
    addDocuments: "Tambah dokumen",
    inScope: "Dalam cakupan",
    excluded: "Dikecualikan",
    // Two different "off" switches exist and must never read the same. This one is
    // global; the rail's per-document ticks narrow only the current conversation.
    excludeHelp:
      "Mengecualikan dokumen di sini berlaku untuk semua percakapan. Untuk membatasi satu percakapan saja, gunakan pemilih cakupan.",
    emptyTitle: "Koleksi ini masih kosong",
    emptyBody: "Unggah berkas di atas, atau tambahkan dokumen yang sudah ada di Pustaka.",
    uploadHere: "Unggah ke {{name}}",
    addDocumentsTo: "Tambah dokumen ke {{name}}",
    noCandidates: "Tidak ada dokumen lain untuk ditambahkan.",
    alreadyFiled: "ada di koleksi lain",
    picked: "{{count}} dipilih",
    addSubmit: "Tambahkan ke koleksi",
    adding: "Menambahkan…",
    addPartial: "{{filed}} ditambahkan, {{failed}} gagal.",
  },
  document: {
    showChunks: "Lihat chunk",
    hideChunks: "Sembunyikan chunk",
    page: "hlm. {{page}}",
    chunksShown: "Menampilkan {{shown}} dari {{total}} chunk.",
    searchable: "Dapat dicari",
    chunkCount_one: "{{count}} chunk",
    chunkCount_other: "{{count}} chunk",
    noChunks: "Belum ada chunk terindeks",
    openOriginal: "Buka dokumen asli",
    openSource: "Buka sumber aslinya",
    notIndexedYet: "Dokumen ini belum terindeks, jadi belum dapat dijawab.",
    jenis: "Jenis",
  },
  errors: {
    generic: "Terjadi kesalahan.",
    network: "Tidak dapat menghubungi layanan.",
    unauthorized: "Sesi berakhir. Silakan masuk kembali.",
    notFound: "Tidak ditemukan.",
    // 404 is deliberately ambiguous on the API: an unknown conversation and a corpus
    // with nothing indexed are the same response. Say both, rather than guess.
    queryNotFound:
      "Percakapan tidak ditemukan, atau belum ada dokumen terindeks yang dapat dijawab.",
    tooLarge: "Konteks terlalu besar untuk dijawab sekaligus. Persempit cakupan atau pertanyaan.",
    upstream: "Model tidak dapat dihubungi. Coba lagi sebentar lagi.",
    emptyQuery: "Pertanyaan tidak boleh kosong.",
    // 400 covers two cases and the API does not distinguish them: an empty question,
    // and a notebook whose sources have all gone. Say both, as the 404 copy does,
    // rather than assert the wrong one.
    badRequest:
      "Pertanyaan kosong, atau notebook ini tidak lagi memiliki sumber. Tambahkan koleksi atau dokumen, lalu coba lagi.",
    retry: "Coba lagi",
    // 409: a conversation belongs to the route (v1 or v2) that first answered in it.
    v2Unavailable: "Mode v2 tidak aktif di server ini",
    v2UnavailableBody:
      "Server ini belum mengaktifkan /query/v2. Mode jawaban dikembalikan ke v1.",
    askWithV1: "Tanyakan dengan mode v1",
    versionConflict:
      "Percakapan ini dijawab dengan mode jawaban lain. Mulai notebook baru untuk memakai mode ini.",
  },
  common: {
    loading: "Memuat…",
    close: "Tutup",
    cancel: "Batal",
    save: "Simpan",
    search: "Cari",
    all: "Semua",
    none: "Tidak ada",
    of: "dari",
  },
};

/** Deliberately NOT `as const`: with literal types every English string becomes a
 *  type error against its Indonesian counterpart, which is the opposite of what a
 *  translation file is for. The shape is what must match, not the words. */
export type Dictionary = typeof id;
